'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import type { SupportedLocale } from '@/lib/translation/types';
import type { SciencePublication } from '../../../payload-types';

/**
 * Renders a list of science publications.
 */
export function SciencePublicationsList({
  locale,
  publications,
  page,
  totalPages
}: {
  locale: SupportedLocale;
  publications: SciencePublication[];
  page: number;
  totalPages: number;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const handlePageChange = useCallback((page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('page', page.toString());
    router.replace(`${pathname}?${params.toString()}`, {
      scroll: false
    });
  }, [pathname, router, searchParams]);
  return <div className="py-12" data-sentry-component="SciencePublicationsList" data-sentry-source-file="scipub.tsx">
      {publications.length === 0 ? <p>No publications found.</p> : <>
          <div className="grid grid-cols-1 md:grid-cols-2 items-stretch lg:grid-cols-4">
            {publications.map(publication => <Link href={`/science/${publication.slug}`} key={publication.id} className="hover:bg-offbluish transition-colors duration-300 px-4 py-5 rounded-2xl flex flex-col">
                <h4 className="md:mb-2 text-offblue text-lg leading-tight">{publication.title}</h4>
                <p className="mt-auto text-gray-500 mb-2 text-sm">{publication.authors}</p>
              </Link>)}
          </div>

          <div className="flex justify-end items-center space-x-4 pt-12">
            <button onClick={() => handlePageChange(page - 1)} className={`group p-2 ${page === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <ChevronLeft className="gray-500" />
            </button>
            <span className="text-offblack text-sm">
              {page} / {totalPages}
            </span>
            <button onClick={() => handlePageChange(page + 1)} className={`group p-2 ${page === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <ChevronRight className="gray-500" />
            </button>
          </div>
        </>}
    </div>;
}